import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Info, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

// This endpoint lives on the AtlasForge web backend (same host as the marketing
// site). In production that's https://atlasforgefinancial.com; in preview it's
// the preview URL. Both resolve to the same FastAPI service.
const API_BASE = (
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_ATLAS_API_BASE ||
  ""
).replace(/\/+$/, "");

async function requestAccountDeletion(payload) {
  const res = await fetch(`${API_BASE}/api/auth/request-deletion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail?.message || data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.detail?.error || data?.error;
    throw err;
  }
  return data;
}

function Field({ label, required, helper, error, children }) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-[#0A1128]">
        {label}
        {required && <span className="text-rose-600 ml-1">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {helper && !error && (
        <span className="block mt-1 text-[12px] text-slate-500">{helper}</span>
      )}
      {error && (
        <span data-testid="email-error" className="block mt-1 text-[12px] text-rose-600 font-medium">
          {error}
        </span>
      )}
    </label>
  );
}

export default function DeleteAccount() {
  const [form, setForm] = useState({ email: "", name: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [success, setSuccess] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setEmailError(null);
    const email = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestAccountDeletion({
        email,
        name: form.name.trim() || undefined,
        reason: form.reason.trim() || undefined,
      });
      setSuccess(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err.status === 400) {
        setEmailError(err.message || "Please enter a valid email address.");
      } else if (err.status === 429) {
        toast.error("Too many requests — try again in a minute.");
      } else {
        toast.error("Something went wrong. Email customersupport@atlasforgefinancial.com");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section className="min-h-[70vh] bg-[#F5F1EB]/60 px-4 py-16">
        <div
          data-testid="delete-account-success"
          className="max-w-[560px] mx-auto bg-[#EEF5F1] border border-[#1A4232]/20 rounded-[12px] p-8"
        >
          <div className="flex justify-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1A4232] text-[#F5F1EB]">
              <CheckCircle2 className="h-7 w-7" strokeWidth={2.25} />
            </span>
          </div>
          <h1 className="mt-4 text-center font-display text-[26px] text-[#0A1128]">
            Request received
          </h1>
          <p className="mt-3 text-center text-[14px] text-slate-700 leading-relaxed">
            {success.message}
          </p>

          <h3 className="mt-6 text-[11px] uppercase tracking-[0.16em] text-[#1A4232] font-semibold">
            What happens next
          </h3>
          <ul className="mt-2 space-y-1.5 text-[13.5px] text-slate-700 list-disc pl-5">
            {(success.next_steps || []).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>

          <div className="mt-6 rounded-[10px] bg-white border border-slate-200 px-4 py-3">
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Your reference</p>
            <code
              data-testid="delete-account-reference"
              className="block mt-1 font-mono text-[13.5px] text-[#0A1128] break-all"
            >
              {success.request_id}
            </code>
          </div>
          <p className="mt-3 text-[12px] text-slate-500">
            Save this reference number — it's the only proof we received your request. Contact support if you need to follow up.
          </p>

          <Link
            to="/"
            data-testid="delete-account-done"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[14px] font-medium px-5 py-2.5 hover:bg-[#0E2A20] transition-colors"
          >
            Done <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[70vh] bg-[#F5F1EB]/60 px-4 py-12 sm:py-16" data-testid="delete-account-page">
      <div className="max-w-[560px] mx-auto">
        {/* Hero */}
        <div className="text-center">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Account deletion</p>
          <h1 className="mt-2 font-display text-[28px] sm:text-[32px] leading-tight text-[#0A1128]">
            Delete your Safe to Spend 365 account
          </h1>
          <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
            Can't log in? Use this form to request deletion. We'll verify and complete within 30 days, as required by GDPR.
          </p>
        </div>

        {/* Info callout — faster path */}
        <div
          data-testid="delete-account-info"
          className="mt-6 rounded-[12px] bg-white border border-slate-200 p-4 flex items-start gap-3"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-600 shrink-0">
            <Info className="h-4 w-4" />
          </span>
          <p className="text-[13px] text-slate-700 leading-relaxed">
            Still have access to the app? It's faster to delete via <strong className="text-[#0A1128]">Settings → Delete account</strong> inside the app. Use this form only if you can't sign in.
          </p>
        </div>

        {/* Warning */}
        <div
          data-testid="delete-account-warning"
          className="mt-4 rounded-[12px] bg-[#FFF8E7] border border-[#E8C97A] p-4 flex items-start gap-3"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#E8C97A]/40 text-[#8A6418] shrink-0">
            <ShieldAlert className="h-4 w-4" />
          </span>
          <p className="text-[13px] text-[#5C4310] leading-relaxed">
            <strong>Deletion is permanent and irreversible.</strong> We'll wipe your account, linked bank accounts (Plaid tokens revoked), transactions, bills, goals, debts, receipts, AI history, achievements, notifications, and any active subscription.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          noValidate
          data-testid="delete-account-form"
          className="mt-6 rounded-[12px] bg-white border border-slate-200 p-6 sm:p-7 space-y-5"
        >
          <Field label="Email" required error={emailError}>
            <input
              type="email"
              required
              autoFocus
              data-testid="delete-account-email"
              value={form.email}
              onChange={(e) => {
                setForm({ ...form, email: e.target.value });
                setEmailError(null);
              }}
              placeholder="you@example.com"
              className="w-full rounded-[10px] border border-slate-300 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#1A4232] focus:ring-2 focus:ring-[#1A4232]/15"
            />
          </Field>

          <Field label="Full name">
            <input
              type="text"
              maxLength={120}
              data-testid="delete-account-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="John Smith"
              className="w-full rounded-[10px] border border-slate-300 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#1A4232] focus:ring-2 focus:ring-[#1A4232]/15"
            />
          </Field>

          <Field
            label="Why are you deleting?"
            helper="Optional — helps us improve"
          >
            <textarea
              rows={3}
              maxLength={1000}
              data-testid="delete-account-reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Tell us why (optional — helps us improve)"
              className="w-full rounded-[10px] border border-slate-300 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#1A4232] focus:ring-2 focus:ring-[#1A4232]/15 resize-y"
            />
            <span className="block mt-1 text-[12px] text-slate-500 text-right">
              {form.reason.length}/1000
            </span>
          </Field>

          <button
            type="submit"
            disabled={submitting}
            data-testid="delete-account-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[14px] font-medium px-5 py-3 hover:bg-[#0E2A20] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                Submit deletion request
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        {/* Footer text */}
        <p className="mt-6 text-center text-[12.5px] text-slate-500">
          Questions?{" "}
          <a
            href="mailto:customersupport@atlasforgefinancial.com"
            data-testid="delete-account-support-email"
            className="text-[#1A4232] underline"
          >
            customersupport@atlasforgefinancial.com
          </a>
        </p>
      </div>
    </section>
  );
}
